import { useEffect, useState } from "react";
import { PriceList } from "@forgepoint/shared";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { usePriceListsStore } from "./priceListsStore";
import PriceListLineTable from "./PriceListLineTable";

interface PriceListEditorSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  priceList: PriceList | null;
  onSaved: () => void;
  onArchived?: (id: string) => void;
  /** Whether the user may edit the price list; when false the sheet is read-only. */
  canEdit: boolean;
}

/** Sheet for editing price list metadata and managing lines via paginated table. */
export default function PriceListEditorSheet({
  open,
  onOpenChange,
  priceList,
  onSaved,
  canEdit,
}: PriceListEditorSheetProps) {
  const { updatePriceList } = usePriceListsStore();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const isArchived = !!priceList?.archivedAt;
  const isSynced = Boolean(priceList?.externalMappings.length);

  useEffect(() => {
    if (!open || !priceList) {
      return;
    }

    setName(priceList.name);
    setDescription(priceList.description ?? "");
  }, [priceList, open]);

  const handleSaveMetadata = async () => {
    if (!priceList) {
      return;
    }

    if (!name.trim()) {
      toast.error("Price list name is required");
      return;
    }

    setIsSaving(true);
    const { priceList: result, error: updateError } = await updatePriceList(
      priceList.id,
      {
        name: name.trim(),
        description: description.trim() || null,
      },
    );
    setIsSaving(false);

    if (result) {
      toast.success("Price list updated");
      onSaved();
    } else {
      toast.error(updateError ?? "Failed to update price list");
    }
  };

  if (!priceList) {
    return null;
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:w-[900px] sm:max-w-[900px] overflow-y-auto">
        <SheetHeader>
          <div className="flex items-center gap-3">
            <SheetTitle>
              {canEdit ? "Edit Price List" : "Price List"}
            </SheetTitle>
            {isArchived && (
              <span className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                Archived
              </span>
            )}
            {isSynced && (
              <Badge
                variant="outline"
                className="text-[10px] font-normal tracking-wider uppercase px-1.5 py-0 border-muted-foreground/25"
              >
                Synced
              </Badge>
            )}
          </div>
          <SheetDescription>
            {isSynced
              ? "This price list is synced from your ERP. Manage it in the source system."
              : canEdit
                ? "Update price list details and manage item pricing."
                : "View price list details and item pricing."}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6 py-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="edit-price-list-name">Name</Label>
              <Input
                id="edit-price-list-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                disabled={!canEdit}
              />
            </div>

            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="edit-price-list-description">Description</Label>
              <Textarea
                id="edit-price-list-description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                rows={2}
                disabled={!canEdit}
              />
            </div>
          </div>

          {canEdit && (
            <SheetFooter className="justify-start">
              <Button
                onClick={handleSaveMetadata}
                disabled={isSaving}
                size="sm"
              >
                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save Details
              </Button>
            </SheetFooter>
          )}

          <div className="border-t pt-6">
            <PriceListLineTable priceListId={priceList.id} canEdit={canEdit} />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
